import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Optional,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  FormControl,
  NgControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { Router } from '@angular/router';
import { FormGroup } from '@ngneat/reactive-forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import {
  BehaviorSubject,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  exhaustMap,
  map,
  startWith,
  tap,
} from 'rxjs';
import { DescontoService } from '../../services/desconto/desconto.service';
import { discountLimitMessage } from '../../utils/discount-limit-message';
import { MAX_SURCHARGE_PERCENT } from '../../utils/discount-limits';
import { ProdutoDesconto } from '@brunsker/libs/portal-do-vendedor/utils/portal-do-vendedor-new-api/lib/models/product.models';
import { UtilsBsHotToastService } from '@brunsker/libs/core/utils/hot-toast/src';
import { NewApiOrcamentoService } from '@brunsker/libs/portal-do-vendedor/utils/portal-do-vendedor-new-api';
import { ResquestBudget } from '@brunsker/libs/portal-do-vendedor/utils/portal-do-vendedor-new-api/lib/models/orcamento.models';

import type { DescontoPrefix, RodaPe } from './models/desconto.models';

@UntilDestroy()
@Component({
  selector: 'bs-desconto',
  templateUrl: './desconto.component.html',
  styleUrls: ['./desconto.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: DescontoComponent,
      multi: true,
    },
  ],
})
export class DescontoComponent implements OnInit, OnChanges {
  value: any;

  ngControl!: NgControl | null;

  onChange?: (value: { value: any; prefix: DescontoPrefix }) => void;
  onTouched?: () => void;
  realPrefix: 'R$' = 'R$';
  percentPrefix: '%' = '%';
  readonly decimalMarkers: ['.', ','] = ['.', ','];
  signal: string = '+';

  descontoRodaPePrefix: DescontoPrefix = '%';
  descautomatico: any;
  unconfirmedValue: any;
  unconfirmedRodaPeValue: string = '';

  writeValue(
    value: { value: any; prefix: DescontoPrefix } | number | string | null,
  ): void {
    this.value = value;

    const controlValue =
      typeof value === 'object' && value !== null ? value.value : value;

    if (typeof value === 'object' && value?.prefix) {
      this.matPrefix = value.prefix;
    }

    const normalizedValue = controlValue ?? 0;
    if (this.descontoControl.value !== normalizedValue) {
      this.descontoControl.setValue(normalizedValue, { emitEvent: false });
    }
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  @Input() id!: string;
  @Input() oldValue!: number;
  @Input() unitaryValue!: number;
  @Input() descontoRodaPe!: FormControl;
  @Input() currentDiscount!: number;
  @Input('maxPercentDiscount') maxPercentDiscount!: ProdutoDesconto;
  numautorizacao: number[] = [];
  descontoControl = new FormControl();
  matPrefix: DescontoPrefix = '%';
  lastRealValue!: string;
  lastPercentValue!: string;
  maxPercent!: number;
  maxReal!: number;
  isConfirmed$$ = new BehaviorSubject(true);
  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    //this.applyDiscout(event);
  }

  constructor(
    private readonly injector: Injector,
    private readonly toast: UtilsBsHotToastService,
    private readonly router: Router,
    private readonly descontoService: DescontoService,
    private readonly orcamentoService: NewApiOrcamentoService,
    @Optional() private readonly controlContainer: ControlContainer,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['maxPercentDiscount'] || changes['unitaryValue']) {
      this.updateMaximumDiscountValues();
    }
  }

  ngOnInit(): void {
    if (!this.oldValue) {
      this.oldValue = 0;
    }
    if (
      this.oldValue &&
      this.oldValue != 0 &&
      !sessionStorage.getItem(this.id)
    ) {
      sessionStorage.setItem(this.id, this.oldValue.toString());
    }

    const numericOldValue = Number(this.oldValue ?? 0);
    this.signal =
      numericOldValue < 0 || Object.is(numericOldValue, -0) ? '+' : '-';
    this.isConfirmed$$.next(true);
    if (!this.descontoRodaPe.value) {
      this.descontoRodaPe.setValue({
        value: 0,
        prefix: this.matPrefix,
      });
    }
    this.configureControlValueAccessor();

    this.setMaxDiscounts();

    this.setDescontoControlValueChanges();

    this.rodaPeValueChanges();
  }

  private setDescontoControlValueChanges() {
    this.descontoControl.valueChanges
      .pipe(
        startWith(this.currentDiscount),
        map((value) => value?.toString()),
        tap((value) => {
          this.unconfirmedValue = value;
          const contPrefix = this.descontoService.PrefixRodapeCont$;

          if (contPrefix.value > 0 && this.matPrefix == '%') {
            contPrefix.setValue(contPrefix.value - 1);
          }
          if (
            (!!this.descontoService.descontoRodapeValue$?.value?.value ||
              this.descontoService.descontoRodapeValue$.value + '' == '') &&
            this.descontoService.descontoRodapeValue$.value.value != 0
          ) {
            this.descontoControl.disable({ emitEvent: false });
            if (this.matPrefix == 'R$')
              this.matPrefix =
                this.matPrefix === this.realPrefix
                  ? this.percentPrefix
                  : this.realPrefix;
          } else {
            this.descontoControl.enable({ emitEvent: false });
          }
        }),
        exhaustMap((value) => {
          return this.isConfirmed$$.pipe(
            map(() => {
              return value;
            }),
          );
        }),
      )
      .subscribe((value) => {
        //  console.log("🚀 ->",
        //    this.isConfirmed$$.value,
        //    'isConfirmed',
        //    value,
        //    typeof value,
        //    this.descontoRodaPe.value,
        //    this.unconfirmedValue
        //  );

        value = this.isConfirmed$$.value ? this.unconfirmedValue : value;

        if (!this.isConfirmed$$.value) return;

        value = typeof value == 'string' ? value.replace(',', '.') : value;

        this.writeDescontoControl(value);
      });
  }

  setOldValue(rodaPeValue: any) {
    if (this.router.url.includes('carrinho')) return;
    const value =
      typeof this.value === 'string' || typeof this.value === 'number'
        ? +this.value
        : this.value.value;

    if (!this.descontoControl.dirty) return;

    const isResetRodaPe = +rodaPeValue == 0 && this.descontoRodaPe.dirty;

    if (!rodaPeValue || isResetRodaPe) {
      this.oldValue = +value;
      sessionStorage.setItem(this.id, this.oldValue.toString());
    }
  }

  verifyOldValue(value: any) {
    if (value?.length === 0 || +value === 0 || value === null) {
      const recoveryLastOldValue = sessionStorage.getItem(this.id) ?? 0;
      const maskedRecoveredValue = (+recoveryLastOldValue).toString();

      this.descontoControl.setValue(maskedRecoveredValue);
      //this.signal = maskedRecoveredValue.includes('-') ? '-' : '+';
      this.write(+recoveryLastOldValue);
    }
  }

  rodaPeValueChanges() {
    this.descontoRodaPe.valueChanges
      .pipe(
        tap((value) => {
          if (typeof value == 'string') {
            this.unconfirmedRodaPeValue = value;
            this.descontoService.descontoRodapeValue$.setValue({
              value: +value.replace(',', '.'),
              prefix: this.matPrefix,
            });
          } else {
            const i = typeof value == 'object' ? value?.value : value;
            this.unconfirmedRodaPeValue = i + '' ?? '0';
            this.descontoService.descontoRodapeValue$.setValue(value);
          }
        }),
        distinctUntilChanged(),
        tap((value) => this.verifyOldValue(value?.value ?? 0)),
        exhaustMap((value) => {
          return this.descontoService.rodaPeIsConfirmed$$.pipe(
            map(() => {
              return typeof value == 'object' ? (value?.value ?? '0') : value;
            }),
          );
        }),
        map<string, RodaPe>((value: string) => {
          const valueRodape = this.descontoService.rodaPeIsConfirmed$$.value
            ? this.unconfirmedRodaPeValue
            : value;
          const prefix =
            (sessionStorage.getItem('prefix') as DescontoPrefix) ?? '%';
          return this.createRodaPeObject(valueRodape, prefix);
        }),

        distinctUntilKeyChanged('value'),
      )
      .subscribe((value) => {
        if (!this.descontoService.rodaPeIsConfirmed$$.value) return;

        if (!value || !value.value) {
          return;
        }

        this.oldValue = sessionStorage.getItem(this.id)
          ? +sessionStorage.getItem(this.id)!
          : 0;

        if (this.matPrefix === 'R$') this.switchPrefix();

        if (value.prefix === 'R$' && this.oldValue >= 0) {
          this.setRodaPeRealValue(value);
          return;
        }

        if (value.prefix === '%' && this.oldValue >= 0) {
          this.setRodaPePercentual(value);
        }
      });
  }

  private setRodaPePercentual(value: RodaPe) {
    const { vlTotal } = this.descontoService.descontoTotalVlTotal$.value;
    let total;
    total = vlTotal;

    if (!isNaN(total) && +value.value > 0 && this.oldValue >= 0) {
      this.unitaryValue = +this.unitaryValue;

      const discountValue = +(this.unitaryValue * (this.oldValue / 100));

      const discountRodaPe =
        (this.unitaryValue - discountValue) * (+value.value / 100);
      const discountTotal = +discountRodaPe + +discountValue;

      const discountToPercent = (+discountTotal * 100) / this.unitaryValue;

      const descontoControl = discountToPercent.toString();

      const userStringify = sessionStorage.getItem('user');
      const user = userStringify && JSON.parse(userStringify);
      const hasMaxDiscount = descontoControl > user.percentualRodape;

      if (hasMaxDiscount) {
        const maxPercent = user.percentualRodape.toString();
        this.descontoControl.setValue((+maxPercent).toFixed(7));
        this.write(+maxPercent);

        return;
      }

      if (this.descontoControl.value === descontoControl) return;

      this.descontoControl.setValue((+descontoControl).toFixed(7));
      this.write(+descontoControl);
    }
  }

  private setRodaPeRealValue(value: RodaPe) {
    const { vlTotal } = this.descontoService.descontoTotalVlTotal$.value;
    let subTotalAux;
    subTotalAux = vlTotal;

    if (typeof value.value === 'string') {
      value.value = value.value.replace(',', '.');
    }

    const realValue = +subTotalAux - Number(value.value);

    const subTotalPercent = (realValue * 100) / +subTotalAux;

    const rodaPePercent = +(100 - +(+subTotalPercent));

    value.value = rodaPePercent;

    if (subTotalAux && value.value > 0 && this.oldValue >= 0) {
      const discountValue = +(this.unitaryValue * (this.oldValue / 100));

      const discountRodaPe = +(
        (this.unitaryValue - discountValue) *
        (+value.value / 100)
      );
      const discountTotal = +discountRodaPe + +discountValue;

      const discountToPercent = +((+discountTotal * 100) / this.unitaryValue);

      const descontoControl = discountToPercent.toString();

      const userStringify = sessionStorage.getItem('user');
      const user = userStringify && JSON.parse(userStringify);
      const hasMaxDiscount = +descontoControl > user.percentualRodape;

      if (hasMaxDiscount) {
        const maxPercent = user.percentualRodape.toString();
        this.descontoControl.setValue(maxPercent);
        this.write(+maxPercent);

        return;
      }

      if (this.descontoControl.value === descontoControl) return;

      this.descontoControl.setValue(Number(descontoControl).toFixed(7));
      this.write(Number(descontoControl));
    }
  }

  private setMaxDiscounts() {
    this.currentDiscount = 0;

    this.updateMaximumDiscountValues();

    if (!this.maxPercentDiscount) return;

    const noRodaPeValue =
      this.descontoRodaPe.value === null ||
      this.descontoRodaPe.value?.length === 0 ||
      (typeof this.descontoRodaPe.value === 'number' &&
        +this.descontoRodaPe.value === 0) ||
      (typeof this.descontoRodaPe.value === 'object' &&
        +this.descontoRodaPe.value.value === 0);

    this.extractAuthorizationKeys(this.maxPercentDiscount);

    if (
      +this.descontoControl.value > this.maxPercent &&
      noRodaPeValue &&
      this.signal == '-'
    ) {
      const { hasToast, hasToastText, warningMessage } =
        this.createPercentageToast(this.maxPercent);
      this.oldValue = this.maxPercent;
      sessionStorage.setItem(this.id, this.oldValue.toString());
      this.descontoControl.setValue(this.maxPercent.toFixed(7));
      this.write(+this.maxPercent);

      if (hasToast && hasToastText)
        this.toast.showWarning(warningMessage, 1500);

      return;
    }

    const hasDiscountAutomatic = this.maxPercentDiscount.descautomatico === 'S';

    if (hasDiscountAutomatic && !this.oldValue) {
      this.descautomatico = this.maxPercentDiscount.descautomatico;
      this.currentDiscount = +this.maxPercent;
      this.oldValue = +this.maxPercent;
      sessionStorage.setItem(this.id, this.maxPercent.toString());
      this.descontoControl.setValue(this.maxPercent.toFixed(7));
    }

    const oldValue = sessionStorage.getItem(this.id);

    const setOldValueCondition =
      oldValue && +oldValue > 0 && noRodaPeValue && !this.descontoControl.dirty;

    if (setOldValueCondition) {
      this.oldValue = +oldValue;
      this.currentDiscount = +oldValue;
      this.descontoControl.setValue((+oldValue).toFixed(7));
      this.write(+oldValue);
    }

    if (typeof this.oldValue === 'number' && this.oldValue >= 0) return;
  }

  private updateMaximumDiscountValues() {
    if (!this.maxPercentDiscount) {
      this.maxPercent = 0;
      this.maxReal = 0;
      return;
    }

    const authorizedDiscount = +this.maxPercentDiscount.percdescautor;
    this.maxPercent =
      authorizedDiscount > 0
        ? authorizedDiscount
        : +this.maxPercentDiscount.percdesc || 0;
    this.extractMaxDiscountReal();
  }

  createRodaPeObject(
    value: string | RodaPe | unknown,
    prefix: DescontoPrefix,
  ): RodaPe {
    if (!value) {
      return {
        value: '0.00',
        prefix: prefix,
      };
    }

    if (typeof value === 'string') {
      return {
        value: value as string,
        prefix: prefix,
      };
    }
    const rodaPe = value as RodaPe;
    if (typeof rodaPe.prefix == 'string') rodaPe.prefix = prefix;

    return rodaPe;
  }

  hasRodaPeDiscount() {
    const isStringOrNumber =
      this.descontoRodaPe.value &&
      typeof this.descontoRodaPe.value == 'string' &&
      this.descontoRodaPe.value.includes(',');

    if (isStringOrNumber) {
      const replacedValue = this.descontoRodaPe.value.replace(',', '.');
      if (+replacedValue > 0) return true;
    }
    const hasDiscountRodaPe = +this.descontoRodaPe.value.value != 0;
    if (hasDiscountRodaPe) return true;

    const isRodaPeObject =
      typeof this.descontoRodaPe.value === 'object' &&
      this.descontoRodaPe.value &&
      this.descontoRodaPe.value.value &&
      +this.descontoRodaPe.value.value > 0;

    if (isRodaPeObject) return true;

    return false;
  }

  getDescontoRodaPeValue() {
    const prefix = sessionStorage.getItem('prefix') ?? '%';

    if (!prefix) {
      sessionStorage.setItem('prefix', '%');
    }
    const userStringify = sessionStorage.getItem('user');
    const user = userStringify && JSON.parse(userStringify);

    const isRodaPeObject =
      this.descontoRodaPe.value &&
      typeof this.descontoRodaPe.value === 'object' &&
      this.descontoRodaPe.value.value;

    const isStringOrNumber =
      typeof this.descontoRodaPe.value === 'string' ||
      typeof this.descontoRodaPe.value === 'number';

    if (prefix === 'R$') {
      if (isRodaPeObject) {
        return +this.conversionUnitaryToPercentSubTotal(
          this.descontoRodaPe.value.value,
        );
      }
      if (isStringOrNumber) {
        return +this.conversionUnitaryToPercentSubTotal({
          value: this.descontoRodaPe.value.value,
          prefix: 'R$',
        });
      }
    }

    const rodaPeValue = !isNaN(+this.descontoRodaPe.value.value)
      ? +this.descontoRodaPe.value.value
      : +this.descontoRodaPe.value.value;

    return rodaPeValue >= user.percentualRodape
      ? user.percentualRodape
      : rodaPeValue;
  }

  validationDescontoControl(value: string | number) {
    const realPrefix = 'R$';
    const percentPrefix = '%';
    const timeToWrite = 150;

    if (this.matPrefix === realPrefix && value !== this.lastRealValue) {
      this.validationDescontoReal(value, timeToWrite);
      return;
    }

    if (this.matPrefix === percentPrefix && value !== this.lastPercentValue) {
      this.validationDescontoPercent(value, timeToWrite);
    }
  }

  private validationDescontoReal(value: string | number, timeToWrite?: number) {
    if (
      (this.signal == '-' && +value < 0) ||
      (this.signal == '+' && +value > 0)
    ) {
      value = +value * -1;
    }

    this.lastRealValue = value.toString();

    const removeComma = value.toString().replace(',', '.');

    if (!this.maxReal || this.maxReal <= 0) {
      this.maxReal = 0;
    }

    if (this.hasRodaPeDiscount()) {
      const maxPercent = +this.maxPercent + this.getDescontoRodaPeValue();
      this.extractMaxDiscountReal(maxPercent);
    }

    const valueGreaterThanHundredPercent =
      +value > this.maxReal ? this.maxReal : removeComma;

    const descontoControlValue = isNaN(+valueGreaterThanHundredPercent)
      ? '0'
      : valueGreaterThanHundredPercent;

    if (this.router.url.includes('compra-finalizada')) return;

    const hasDiscountInPage =
      this.router.url.includes('/carrinho') ||
      this.router.url.includes('editar-orcamento');

    if (!hasDiscountInPage) return;

    this.lastRealValue = descontoControlValue.toString();

    this.currentDiscount = +descontoControlValue;

    this.lastRealValue = descontoControlValue.toString();

    if (+value > this.maxReal) {
      const { hasToast, hasToastText, warningMessage } = this.createRealToast();
      this.write(+this.maxReal);
      this.descontoControl.setValue((+this.maxReal).toFixed(7));
      sessionStorage.setItem(this.id, this.maxPercent.toString());
      if (hasToast && hasToastText)
        this.toast.showWarning(warningMessage, 1500);
      return;
    }
    const discountToPercent = +(
      (+valueGreaterThanHundredPercent * 100) /
      this.unitaryValue
    );
    sessionStorage.setItem(this.id, discountToPercent.toString());

    this.write(+descontoControlValue);
  }

  private createRealToast() {
    const warningMessage = discountLimitMessage(this.maxReal, 'R$');

    const hasToast = document.querySelectorAll(
      '.bs.warning .hot-toast-message dynamic-view div',
    );

    const hasToastText = [...(hasToast as any)].every((toastText) => {
      return toastText?.innerHTML !== warningMessage;
    });
    return { hasToast, hasToastText, warningMessage };
  }

  private validationDescontoPercent(
    value: string | number,
    timeToWrite: number,
  ) {
    if (!value) value = 0;

    if (
      (this.signal == '-' && +value < 0) ||
      (this.signal == '+' && +value > 0)
    ) {
      value = +value * -1;
    }

    this.lastPercentValue = value.toString();

    const hasPercent = this.matPrefix.includes('%');
    const addPercentage = `${value}${hasPercent ? '' : this.matPrefix}`;
    const removePercent = addPercentage.replace('%', '');
    const removeComma = removePercent.replace(',', '.');

    if (!this.maxPercent || this.maxPercent <= 0) {
      this.maxPercent = 0;
    }

    const maxPercent = this.hasRodaPeDiscount()
      ? +this.maxPercent + this.getDescontoRodaPeValue()
      : this.maxPercent;

    const valueGreaterThanHundredPercent =
      +removeComma > maxPercent ? maxPercent : addPercentage;

    const descontoControlValue = isNaN(+valueGreaterThanHundredPercent)
      ? '0'
      : valueGreaterThanHundredPercent;

    if (this.router.url.includes('compra-finalizada')) return;

    const hasDiscountInPage =
      this.router.url.includes('/carrinho') ||
      this.router.url.includes('editar-orcamento');

    if (!hasDiscountInPage) return;

    this.lastRealValue = descontoControlValue.toString();

    this.currentDiscount = +descontoControlValue;

    if (Number(value) > maxPercent) {
      const { hasToast, hasToastText, warningMessage } =
        this.createPercentageToast(maxPercent);

      if (hasToast && hasToastText && this.descontoControl.dirty)
        this.toast.showWarning(warningMessage, 1500);
      const maskedValue = +maxPercent.toString();

      this.write(Number(maskedValue));
      this.descontoControl.setValue(Number(maskedValue).toFixed(7));

      return;
    }

    this.descontoControl.setValue(Number(value));
    this.write(Number(value));
    sessionStorage.setItem(this.id, this.value.value.toString());
  }

  private createPercentageToast(maxPercent: number) {
    const warningMessage = discountLimitMessage(maxPercent, '%');

    const hasToast = document.querySelectorAll(
      '.bs.warning .hot-toast-message dynamic-view div',
    );

    const hasToastText = [...(hasToast as any)].every((toastText) => {
      return toastText?.innerHTML !== warningMessage;
    });
    return { hasToast, hasToastText, warningMessage };
  }

  private conversionUnitaryToPercentSubTotal(value: RodaPe) {
    const hasDoubleComma =
      value.value.toString().length >= 6 &&
      value.value.toString().match(/\./g)?.length === 2;
    if (hasDoubleComma) {
      const replaceReal = value.value.toString().replace('.', '');
      const valueNumber = replaceReal.substring(0, replaceReal.length - 2);
      const valueFloat = replaceReal.substring(replaceReal.length - 2);
      const realValue = `${valueNumber}.${valueFloat}`;

      value.value = realValue;

      if (value.value.includes('..')) {
        value.value = value.value.replace('..', '.');
      }
    }

    const numberValue = +value.value.toString().replace(',', '.');

    const expression =
      +sessionStorage.getItem('unitaryValueWithDiscount')! - numberValue;

    const multiplyExpression = expression * 100;

    const percentage =
      multiplyExpression / +sessionStorage.getItem('unitaryValueWithDiscount')!;

    return 100 - percentage;
  }

  private extractMaxDiscountReal(maxPercent?: number) {
    const maxInReal =
      this.unitaryValue * ((maxPercent ? maxPercent : this.maxPercent) / 100);

    const numberToFixed = maxInReal;

    const greaterThanToFixed = maxInReal > +numberToFixed ? 0.0 : 0.0;

    const equalsZero = numberToFixed === 0.0;

    const roundDown = equalsZero
      ? numberToFixed
      : +numberToFixed - greaterThanToFixed;

    this.maxReal = +roundDown;
  }

  private extractAuthorizationKeys(maxPercentDiscount: ProdutoDesconto) {
    const numautorizacao = sessionStorage.getItem('numautorizacao');

    const numaturozicaolist =
      (numautorizacao && JSON.parse(numautorizacao)) || [];

    const hasNumautorizacao =
      maxPercentDiscount.numautorizacao &&
      maxPercentDiscount.numautorizacao > 0 &&
      !numaturozicaolist.includes(maxPercentDiscount.numautorizacao);

    if (hasNumautorizacao) {
      numaturozicaolist.push(maxPercentDiscount.numautorizacao);
      const numautorizacaoStringify = JSON.stringify(numaturozicaolist);
      sessionStorage.setItem('numautorizacao', numautorizacaoStringify);
    }
  }

  private writeDescontoControl(value: string | number) {
    value = Number(value);

    if (!value) {
      this.write(0);
      this.descontoControl.setValue(0);
      return;
    }
    this.validationDescontoControl(value);
  }

  private write(value: number) {
    const maximumSurcharge = this.matPrefix === '%'
      ? MAX_SURCHARGE_PERCENT
      : Number((this.unitaryValue * MAX_SURCHARGE_PERCENT / 100).toFixed(7));

    if (Number.isFinite(maximumSurcharge) && maximumSurcharge >= 0 && value < -maximumSurcharge) {
      value = -maximumSurcharge;
      this.unconfirmedValue = value;
      this.currentDiscount = value;
      sessionStorage.setItem(this.id, (-MAX_SURCHARGE_PERCENT).toString());
      const warningMessage = discountLimitMessage(maximumSurcharge, this.matPrefix, true);
      const hasSameToast = Array.from(document.querySelectorAll(
        '.bs.warning .hot-toast-message dynamic-view div',
      )).some((toastText) => toastText.innerHTML === warningMessage);
      if (!hasSameToast) this.toast.showWarning(warningMessage, 1500);
    }

    this.onChange?.apply(this, [
      {
        value: value,
        prefix: this.matPrefix,
      },
    ]);
    this.writeValue({
      value: value,
      prefix: this.matPrefix,
    });
  }

  private configureControlValueAccessor() {
    this.ngControl = this.injector.get(NgControl);

    if (this.ngControl && this.ngControl.control) {
      this.descontoControl.validator = this.ngControl.control.validator;
    }

    const hasControl = this.controlContainer && this.controlContainer.control;

    if (!hasControl && this.controlContainer) {
      const formGroup = this.controlContainer.control as FormGroup<any>;
      const key = this.ngControl!.name as keyof FormGroup<any>;
      const control = formGroup.get(key) as AbstractControl;
      this.descontoControl.validator = control.validator;
    }
  }

  switchPrefix() {
    if (this.hasRodaPeDiscount()) return;
    const currentControlValue = this.descontoControl.value.toString();

    this.matPrefix =
      this.matPrefix === this.realPrefix ? this.percentPrefix : this.realPrefix;

    const removeComma = currentControlValue.toString().replace(',', '.');
    const calcTransformedPercentage =
      this.matPrefix === this.realPrefix
        ? removeComma
        : (removeComma * 100) / this.unitaryValue;
    const toFixedPercentage = calcTransformedPercentage;
    const contPrefix = this.descontoService.PrefixRodapeCont$;

    if (contPrefix.value > 0 && this.matPrefix == '%') {
      contPrefix.setValue(contPrefix.value - 1);
    }
    if (this.matPrefix === this.realPrefix) {
      contPrefix.setValue(contPrefix.value + 1);
      const removePercent = currentControlValue.replace('%', '');
      const removePercentComma = removePercent.replace(',', '.');
      const calcTransformedReal =
        this.unitaryValue * (removePercentComma / 100);
      const toFixedReal = calcTransformedReal;

      this.descontoControl.setValue(toFixedReal.toFixed(7).replace('-', ''));
    } else {
      this.descontoControl.setValue(toFixedPercentage.toFixed(7));
    }
    const appliedValue = Math.abs(Number(this.descontoControl.value)) *
      (this.signal === '+' ? -1 : 1);
    sessionStorage.setItem(this.id, (
      this.matPrefix === 'R$' ? appliedValue * 100 / this.unitaryValue : appliedValue
    ).toString());
    this.descontoControl.markAsDirty();
    return;
  }

  switchSignal() {
    this.signal = this.signal === '-' ? '+' : '-';
    this.isConfirmed$$.next(true);

    const normalizedValue = Number(
      this.descontoControl.value?.toString().replace(',', '.') ?? 0,
    );
    const discountValue = Number.isFinite(normalizedValue)
      ? normalizedValue
      : 0;
    const signedValue =
      this.signal === '+' ? -Math.abs(discountValue) : Math.abs(discountValue);

    this.unconfirmedValue = signedValue;
    sessionStorage.setItem(
      this.id,
      this.signal === '+' && signedValue === 0 ? '-0' : signedValue.toString(),
    );
    this.descontoControl.setValue(
      signedValue === 0 ? 0 : signedValue.toFixed(7),
      { emitEvent: false },
    );
    this.write(signedValue);
  }

  applyDiscout(event: KeyboardEvent | any) {
    this.setMaxDiscounts();
    const isRodaPeInput = event.target?.id === 'descontoRodape';
    if (event.key === 'Enter' || event.type === 'focusout') {
      if (isRodaPeInput) {
        this.descontoService.rodaPeIsConfirmed$$.next(true);
        return;
      }
      this.isConfirmed$$.next(true);

      if (!this.hasRodaPeDiscount())
        this.descontoService.changeDescontoItem$$.next(true);

      if (
        (this.signal == '-' && +this.unconfirmedValue < 0) ||
        (this.signal == '+' && +this.unconfirmedValue > 0)
      ) {
        this.unconfirmedValue = this.unconfirmedValue * -1;
        const valueToSet = (+this.unconfirmedValue)
          .toFixed(7)
          .replace(',', '.');
        this.descontoControl.setValue(valueToSet);
      }
      if (this.matPrefix === 'R$') return;
      sessionStorage.setItem(this.id, this.unconfirmedValue);
      return;
    }
    if (
      event.key === 'Escape' &&
      document.activeElement instanceof HTMLInputElement
    ) {
      if (isRodaPeInput) {
        this.descontoRodaPe.setValue(this.descontoRodaPe.value);
        return;
      }
      this.descontoControl.setValue(this.oldValue.toFixed(7));
      return;
    }

    this.isConfirmed$$.next(false);

    if (isRodaPeInput && document.activeElement instanceof HTMLInputElement) {
      this.descontoService.rodaPeIsConfirmed$$.next(false);
      return;
    }
  }

  alertRodape() {
    if (!this.descontoRodaPe.value.value) return;
    this.toast.showInfo('Remova o desconto de rodape para alterar o valor');
  }

  getValueBudget(budget: ResquestBudget) {
    return this.orcamentoService
      .calculoOrcamento(budget)
      .pipe(map((resultado) => resultado));
  }
}
